import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { baseurImage } from '../../Baseurl'
import axiosInstance from '../../api/axiosInstance'
import { getCurrentUserId } from '../../utils/functions'

const COLORS = {
  gray: '#5B6B79',
  blue: '#96C5F7',
  red: '#9E2B25',
  orange: '#F29E4C',
  green: '#2CA87F',
}

const TABS = [
  { id: 'staff',  label: 'Staff',   icon: 'ti-users',       color: COLORS.blue  },
  { id: 'doctor', label: 'Doctors', icon: 'ti-stethoscope', color: COLORS.green },
  { id: 'nurse',  label: 'Nurses',  icon: 'ti-heart',       color: COLORS.orange },
]

const itemsPerPage = 10

const getUserImageUrl = (user) => {
  if (user?.personalPhoto && user.personalPhoto !== 'null' && user.personalPhoto !== 'undefined')
    return `${baseurImage}${user.personalPhoto}`
  return '../assets/images/user/avatar-1.jpg'
}

const Avatar = ({ user }) => (
  <img
    src={getUserImageUrl(user)}
    alt="user"
    onError={(e) => { e.target.src = '../assets/images/user/avatar-1.jpg' }}
    style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${COLORS.blue}` }}
  />
)

const Badge = ({ label, color, bg }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
    color: color || '#fff', background: bg || COLORS.gray, whiteSpace: 'nowrap',
  }}>{label}</span>
)

const GenderBadge = ({ gender }) => {
  const g = (gender || '').toLowerCase()
  return <Badge label={gender || '—'} bg={g === 'male' ? '#d0e8ff' : g === 'female' ? '#fce4ec' : '#eee'} color={g === 'male' ? '#1565c0' : g === 'female' ? '#880e4f' : '#555'} />
}

function Pagination({ currentPage, totalPages, setCurrentPage }) {
  if (!totalPages || totalPages <= 1) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
        disabled={currentPage === 1}
        style={paginBtn(currentPage === 1)}
      >
        <i className="ti ti-chevron-left" />
      </button>
      <span style={{ fontSize: 12, color: COLORS.gray, fontWeight: 600 }}>
        {currentPage} / {totalPages}
      </span>
      <button
        onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
        disabled={currentPage === totalPages}
        style={paginBtn(currentPage === totalPages)}
      >
        <i className="ti ti-chevron-right" />
      </button>
    </div>
  )
}

const paginBtn = (disabled) => ({
  width: 28, height: 28, borderRadius: 6, border: `1px solid ${COLORS.blue}`,
  background: disabled ? '#f5f7fa' : COLORS.blue, color: disabled ? '#aaa' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontSize: 13,
})

const SearchBox = ({ value, onChange, placeholder }) => (
  <div style={{ position: 'relative', maxWidth: 260 }}>
    <i className="ti ti-search" style={{
      position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
      color: COLORS.gray, fontSize: 14, pointerEvents: 'none',
    }} />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        paddingLeft: 32, paddingRight: 10, height: 34, fontSize: 13,
        border: `1px solid #dde3eb`, borderRadius: 8, width: '100%',
        outline: 'none', color: COLORS.gray,
      }}
    />
  </div>
)

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 10px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 13, color: '#2d3748', padding: '7px 10px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}

const ActionBtn = ({ onClick }) => (
  <button
    onClick={onClick}
    title="View Permissions"
    style={{
      width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer',
      background: `${COLORS.blue}22`, color: COLORS.blue, display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontSize: 15,
      transition: 'background 0.2s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = COLORS.blue + '44'}
    onMouseLeave={e => e.currentTarget.style.background = COLORS.blue + '22'}
  >
    <i className="ti ti-shield-lock" />
  </button>
)

const Security = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('staff')
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [logoutMinutes, setLogoutMinutes] = useState(() => {
    const bs = JSON.parse(localStorage.getItem('businessSetting') || '{}')
    return parseInt(bs?.auto_logout_minutes || 30, 10)
  })
  const [currentLogoutMinutes, setCurrentLogoutMinutes] = useState(() => {
    const bs = JSON.parse(localStorage.getItem('businessSetting') || '{}')
    return parseInt(bs?.auto_logout_minutes || 30, 10)
  })
  const [savingLogout, setSavingLogout] = useState(false)

  const [showRecordLockModal, setShowRecordLockModal] = useState(false)
  const [recordLockHours, setRecordLockHours] = useState(() => {
    const bs = JSON.parse(localStorage.getItem('businessSetting') || '{}')
    return bs?.record_lock_hours ?? ''
  })
  const [savingRecordLock, setSavingRecordLock] = useState(false)

  const [allStaff, setAllStaff]     = useState([])
  const [allDoctors, setAllDoctors] = useState([])
  const [allNurses, setAllNurses]   = useState([])

  const [searchStaff, setSearchStaff]   = useState('')
  const [searchDoctor, setSearchDoctor] = useState('')
  const [searchNurse, setSearchNurse]   = useState('')

  const [pageStaff, setPageStaff]   = useState(1)
  const [pageDoctor, setPageDoctor] = useState(1)
  const [pageNurse, setPageNurse]   = useState(1)

  useEffect(() => {
    const bs = JSON.parse(localStorage.getItem('businessSetting') || '{}')
    const doctorId = bs?.doctor_id
    if (!doctorId) return
    axiosInstance.get(`getBusinessSettingByDoctorId/${doctorId}`)
      .then(res => {
        const val = res.data?.data?.auto_logout_minutes
        if (val != null) {
          setCurrentLogoutMinutes(parseInt(val, 10))
          setLogoutMinutes(parseInt(val, 10))
        }
      })
      .catch(e => console.error(e))
  }, [])

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [s, d, n] = await Promise.all([
          axiosInstance.get('getAllStaff'),
          axiosInstance.get('getAllDoctors'),
          axiosInstance.get('getAllNurses'),
        ])
        if (s.data?.success) setAllStaff(s.data.data || [])
        if (d.data?.success) setAllDoctors(d.data.data || [])
        if (n.data?.success) setAllNurses(n.data.data || [])
      } catch (e) { console.error(e) }
    }
    fetchAll()
  }, [])

  const openPermissions = (user) => navigate('/Admin/staff-permissions', { state: { account: user } })

  const filter = (list, q, fields) =>
    list.filter(item => fields.some(f => String(item[f] || '').toLowerCase().includes(q.toLowerCase())))

  const filteredStaff   = filter(allStaff,   searchStaff,  ['displayName', 'email', 'civilId', 'phoneNumber'])
  const filteredDoctors = filter(allDoctors, searchDoctor, ['displayName', 'fullName', 'email', 'specialty', 'civilId', 'phoneNumber'])
  const filteredNurses  = filter(allNurses,  searchNurse,  ['displayName', 'fullName', 'email', 'civilId', 'phoneNumber'])

  const paginate = (list, page) => list.slice((page - 1) * itemsPerPage, page * itemsPerPage)
  const totalPages = (list) => Math.ceil(list.length / itemsPerPage) || 1

  const saveLogoutMinutes = async () => {
    const val = parseInt(logoutMinutes, 10)
    if (!val || val < 1) return
    const bs = JSON.parse(localStorage.getItem('businessSetting') || '{}')
    const doctorId = bs?.doctor_id
    if (!doctorId) {
      Swal.fire('Error', 'Could not determine business account. Please log in again.', 'error')
      return
    }
    setSavingLogout(true)
    try {
      await axiosInstance.put(`updateBusinessSetting/${doctorId}`, { auto_logout_minutes: val })
      bs.auto_logout_minutes = val
      localStorage.setItem('businessSetting', JSON.stringify(bs))
      setCurrentLogoutMinutes(val)
      setShowLogoutModal(false)
    } catch (e) {
      console.error(e)
      Swal.fire('Error', 'Failed to save auto-logout timer. Please try again.', 'error')
    } finally {
      setSavingLogout(false)
    }
  }

  const saveRecordLockHours = async () => {
    const val = parseInt(recordLockHours, 10)
    if (isNaN(val) || val < 1) return
    const doctorId = getCurrentUserId()
    if (!doctorId) return
    setSavingRecordLock(true)
    try {
      await axiosInstance.put(`updateBusinessSetting/${doctorId}`, { record_lock_hours: val })
      setShowRecordLockModal(false)
    } catch (e) {
      console.error(e)
    } finally {
      setSavingRecordLock(false)
    }
  }

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              title="Go back"
              style={{
                width: 30, height: 30, borderRadius: 8, border: `1px solid #e8ecf0`,
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, marginRight: 4, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.red}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-shield-check" style={{ color: COLORS.red, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Access & Permissions</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage staff, doctor and nurse access control</div>
            </div>
          </div>

          {/* Action Bar */}
          <div style={{
            padding: '10px 20px', borderBottom: '1px solid #e8ecf0',
            background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <button
              className="bgBtn btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: '5px 12px' }}
              onClick={() => setShowLogoutModal(true)}
            >
              <i className="ti ti-clock" style={{ fontSize: 15 }} />
              Auto-Logout
            </button>
            <button
              className="bgBtn btn-sm d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: '5px 12px' }}
              onClick={() => setShowRecordLockModal(true)}
            >
              <i className="ti ti-lock" style={{ fontSize: 15 }} />
              Record Lock
            </button>
          </div>

          {/* Auto-Logout Modal */}
          {showLogoutModal && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 1050,
              background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }} onClick={() => setShowLogoutModal(false)}>
              <div style={{
                background: '#fff', borderRadius: 12, width: 340, padding: '24px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)', position: 'relative',
              }} onClick={e => e.stopPropagation()}>

                {/* Modal Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.orange}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="ti ti-clock" style={{ color: COLORS.orange, fontSize: 18 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Auto-Logout Timer</div>
                    <div style={{ fontSize: 11, color: COLORS.gray }}>Set inactivity timeout before automatic logout</div>
                  </div>
                  <button onClick={() => setShowLogoutModal(false)} style={{
                    marginLeft: 'auto', width: 26, height: 26, borderRadius: 6,
                    border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14,
                  }}>
                    <i className="ti ti-x" />
                  </button>
                </div>

                {/* Input */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>
                    Inactivity timeout (minutes)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="number"
                      min={1}
                      max={480}
                      value={logoutMinutes}
                      onChange={e => setLogoutMinutes(e.target.value)}
                      style={{
                        flex: 1, height: 38, borderRadius: 8, border: `1.5px solid #dde3eb`,
                        padding: '0 12px', fontSize: 15, fontWeight: 600, color: '#1a202c',
                        outline: 'none', textAlign: 'center',
                      }}
                      onFocus={e => e.target.style.borderColor = COLORS.orange}
                      onBlur={e => e.target.style.borderColor = '#dde3eb'}
                    />
                    <span style={{ fontSize: 13, color: COLORS.gray, whiteSpace: 'nowrap' }}>min</span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 6 }}>
                    Currently set to <strong>{currentLogoutMinutes} min</strong>. Takes effect on next login.
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowLogoutModal(false)} style={{
                    padding: '7px 16px', fontSize: 13, borderRadius: 8,
                    border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  }}>
                    Cancel
                  </button>
                  <button onClick={saveLogoutMinutes} className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ padding: '7px 16px', fontSize: 13 }} disabled={savingLogout}>
                    {savingLogout && <i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />}
                    {savingLogout ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Record Lock Modal */}
          {showRecordLockModal && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 1050,
              background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }} onClick={() => setShowRecordLockModal(false)}>
              <div style={{
                background: '#fff', borderRadius: 12, width: 340, padding: '24px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)', position: 'relative',
              }} onClick={e => e.stopPropagation()}>

                {/* Modal Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.red}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="ti ti-lock" style={{ color: COLORS.red, fontSize: 18 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Record Lock</div>
                    <div style={{ fontSize: 11, color: COLORS.gray }}>Records become read-only after this period</div>
                  </div>
                  <button onClick={() => setShowRecordLockModal(false)} style={{
                    marginLeft: 'auto', width: 26, height: 26, borderRadius: 6,
                    border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14,
                  }}>
                    <i className="ti ti-x" />
                  </button>
                </div>

                {/* Input */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>
                    Lock records after (hours)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="number"
                      min={1}
                      value={recordLockHours}
                      onChange={e => setRecordLockHours(e.target.value)}
                      placeholder="e.g. 24"
                      style={{
                        flex: 1, height: 38, borderRadius: 8, border: `1.5px solid #dde3eb`,
                        padding: '0 12px', fontSize: 15, fontWeight: 600, color: '#1a202c',
                        outline: 'none', textAlign: 'center',
                      }}
                      onFocus={e => e.target.style.borderColor = COLORS.red}
                      onBlur={e => e.target.style.borderColor = '#dde3eb'}
                    />
                    <span style={{ fontSize: 13, color: COLORS.gray, whiteSpace: 'nowrap' }}>hrs</span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowRecordLockModal(false)} style={{
                    padding: '7px 16px', fontSize: 13, borderRadius: 8,
                    border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                  }}>
                    Cancel
                  </button>
                  <button onClick={saveRecordLockHours} className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ padding: '7px 16px', fontSize: 13 }} disabled={savingRecordLock}>
                    {savingRecordLock && <i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />}
                    {savingRecordLock ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0', borderBottom: '1px solid #e8ecf0', background: '#fafbfc' }}>
            {TABS.map(tab => {
              const active = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px',
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    color: active ? tab.color : COLORS.gray,
                    background: active ? '#fff' : 'transparent',
                    border: '1px solid', borderColor: active ? '#e8ecf0' : 'transparent',
                    borderBottom: active ? '2px solid ' + tab.color : '2px solid transparent',
                    borderRadius: '8px 8px 0 0', cursor: 'pointer',
                    marginBottom: -1, transition: 'all 0.15s',
                  }}
                >
                  <i className={`ti ${tab.icon}`} style={{ fontSize: 15 }} />
                  {tab.label}
                  <span style={{
                    fontSize: 10, fontWeight: 700, minWidth: 18, height: 18,
                    borderRadius: 9, background: active ? tab.color : '#e8ecf0',
                    color: active ? '#fff' : COLORS.gray,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                  }}>
                    {tab.id === 'staff' ? allStaff.length : tab.id === 'doctor' ? allDoctors.length : allNurses.length}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Tab Panels */}
          <div style={{ padding: '14px 20px' }}>

            {/* STAFF */}
            {activeTab === 'staff' && (
              <TablePanel
                data={paginate(filteredStaff, pageStaff)}
                columns={[
                  { label: '', key: 'photo', render: (item) => <Avatar user={item} /> },
                  { label: 'Name', key: 'name', render: (item) => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.displayName || '—'}</div>
                      <div style={{ fontSize: 11, color: COLORS.gray }}>{item.email || ''}</div>
                    </div>
                  )},
                  { label: 'DOB', key: 'dob', render: (item) => <span style={{ fontSize: 12 }}>{item.dateOfBirth ? new Date(item.dateOfBirth).toLocaleDateString('en-GB') : '—'}</span> },
                  { label: 'License', key: 'lic', render: (item) => <span style={{ fontSize: 12 }}>{item.licenseId || '—'}</span> },
                  { label: 'Civil ID', key: 'civil', render: (item) => <span style={{ fontSize: 12 }}>{item.civilId || '—'}</span> },
                  { label: 'Phone', key: 'phone', render: (item) => <span style={{ fontSize: 12 }}>{item.phoneNumber || '—'}</span> },
                  { label: 'Gender', key: 'gender', render: (item) => <GenderBadge gender={item.gender} /> },
                  { label: '', key: 'action', render: (item) => <ActionBtn onClick={() => openPermissions(item)} /> },
                ]}
                search={<SearchBox value={searchStaff} onChange={e => { setSearchStaff(e.target.value); setPageStaff(1) }} placeholder="Search staff..." />}
                pagination={<Pagination currentPage={pageStaff} totalPages={totalPages(filteredStaff)} setCurrentPage={setPageStaff} />}
                total={filteredStaff.length}
                accentColor={COLORS.blue}
              />
            )}

            {/* DOCTORS */}
            {activeTab === 'doctor' && (
              <TablePanel
                data={paginate(filteredDoctors, pageDoctor)}
                columns={[
                  { label: '', key: 'photo', render: (item) => <Avatar user={item} /> },
                  { label: 'Name', key: 'name', render: (item) => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.displayName || item.fullName || '—'}</div>
                      <div style={{ fontSize: 11, color: COLORS.gray }}>{item.email || ''}</div>
                    </div>
                  )},
                  { label: 'Specialty', key: 'spec', render: (item) => (
                    item.specialty ? <Badge label={item.specialty} bg={`${COLORS.green}18`} color={COLORS.green} /> : <span style={{ color: '#aaa' }}>—</span>
                  )},
                  { label: 'DOB', key: 'dob', render: (item) => <span style={{ fontSize: 12 }}>{item.dateOfBirth ? new Date(item.dateOfBirth).toLocaleDateString('en-GB') : '—'}</span> },
                  { label: 'License', key: 'lic', render: (item) => <span style={{ fontSize: 12 }}>{item.licenseId || '—'}</span> },
                  { label: 'Gender', key: 'gender', render: (item) => <GenderBadge gender={item.gender} /> },
                  { label: '', key: 'action', render: (item) => <ActionBtn onClick={() => openPermissions(item)} /> },
                ]}
                search={<SearchBox value={searchDoctor} onChange={e => { setSearchDoctor(e.target.value); setPageDoctor(1) }} placeholder="Search doctors..." />}
                pagination={<Pagination currentPage={pageDoctor} totalPages={totalPages(filteredDoctors)} setCurrentPage={setPageDoctor} />}
                total={filteredDoctors.length}
                accentColor={COLORS.green}
              />
            )}

            {/* NURSES */}
            {activeTab === 'nurse' && (
              <TablePanel
                data={paginate(filteredNurses, pageNurse)}
                columns={[
                  { label: '', key: 'photo', render: (item) => <Avatar user={item} /> },
                  { label: 'Name', key: 'name', render: (item) => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{item.displayName || item.fullName || '—'}</div>
                      <div style={{ fontSize: 11, color: COLORS.gray }}>{item.email || ''}</div>
                    </div>
                  )},
                  { label: 'DOB', key: 'dob', render: (item) => <span style={{ fontSize: 12 }}>{item.dateOfBirth ? new Date(item.dateOfBirth).toLocaleDateString('en-GB') : '—'}</span> },
                  { label: 'Civil ID', key: 'civil', render: (item) => <span style={{ fontSize: 12 }}>{item.civilId || '—'}</span> },
                  { label: 'Phone', key: 'phone', render: (item) => <span style={{ fontSize: 12 }}>{item.phoneNumber || '—'}</span> },
                  { label: 'Gender', key: 'gender', render: (item) => <GenderBadge gender={item.gender} /> },
                  { label: '', key: 'action', render: (item) => <ActionBtn onClick={() => openPermissions(item)} /> },
                ]}
                search={<SearchBox value={searchNurse} onChange={e => { setSearchNurse(e.target.value); setPageNurse(1) }} placeholder="Search nurses..." />}
                pagination={<Pagination currentPage={pageNurse} totalPages={totalPages(filteredNurses)} setCurrentPage={setPageNurse} />}
                total={filteredNurses.length}
                accentColor={COLORS.orange}
              />
            )}

          </div>
        </div>
      </div>
    </div>
  )
}

function TablePanel({ data, columns, search, pagination, total, accentColor }) {
  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        {search}
        <span style={{ fontSize: 11, color: COLORS.gray }}>
          {total} record{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key} style={{ ...thStyle, borderLeft: col.key === 'photo' ? `3px solid ${accentColor}` : undefined }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ ...tdStyle, textAlign: 'center', color: COLORS.gray, padding: '32px 0' }}>
                  <i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
                  No records found
                </td>
              </tr>
            ) : data.map((item, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}
                onMouseEnter={e => e.currentTarget.style.background = `${accentColor}0d`}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
              >
                {columns.map(col => (
                  <td key={col.key} style={tdStyle}>{col.render(item)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        {pagination}
      </div>
    </div>
  )
}

export default Security
